import { type WidgetSize, widgets } from './registry';

/**
 * 组件面板的布局模型：一份有序的 widget 实例列表（引用注册表的
 * key + iOS 式尺寸档位），持久在 localStorage——纯客户端偏好，
 * 不进服务端数据根。结构升级靠 key 里的版本号（v2 从宽/高两维
 * 改为尺寸档位，旧数据不迁移，回默认布局）；载入时做归一化
 * （剔除未知类型、去重），注册表新增的 widget 自动补到末尾。
 */

/** 布局里的一个 widget 实例。 */
export interface WidgetLayoutItem {
  /** 对应 WidgetDef.key。 */
  key: string;
  /** 尺寸档位：小 / 中 / 大。 */
  size: WidgetSize;
}

const STORAGE_KEY = 'nota.home.widgets.v2';

/** 默认布局 = 注册表顺序 + 各 widget 自报的默认档位。 */
export const defaultLayout = (): WidgetLayoutItem[] =>
  widgets.map((w) => ({ key: w.key, size: w.size }));

/** 从 localStorage 读布局并归一化；没有 / 坏了就回默认布局。 */
export function loadLayout(): WidgetLayoutItem[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
  } catch {
    parsed = null;
  }
  if (!Array.isArray(parsed)) return defaultLayout();

  const items: WidgetLayoutItem[] = [];
  const seen = new Set<string>();
  for (const raw of parsed) {
    if (typeof raw !== 'object' || raw === null) continue;
    const { key, size } = raw as Partial<WidgetLayoutItem>;
    if (typeof key !== 'string' || seen.has(key)) continue;
    const def = widgets.find((w) => w.key === key);
    if (def === undefined) continue;
    seen.add(key);
    items.push({
      key,
      size: size === 'sm' || size === 'md' || size === 'lg' ? size : def.size,
    });
  }
  // 注册表里新加的 widget 类型自动补进（保持注册顺序）
  for (const w of widgets) {
    if (!seen.has(w.key)) items.push({ key: w.key, size: w.size });
  }
  return items;
}

/** 布局落盘（隐私模式等存不了就静默放弃，只影响下次记忆）。 */
export function saveLayout(items: WidgetLayoutItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // 忽略：布局记忆是尽力而为
  }
}
